import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FiLogOut, FiUser, FiMapPin, FiShoppingBag, FiMenu, FiX } from "react-icons/fi";

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if screen is mobile on initial load
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    checkMobile();

    // Add event listener for resize
    window.addEventListener('resize', checkMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/login");
  };

  const getLinkClass = (path) => {
    const isActive = location.pathname === path;
    return `flex items-center gap-2 py-2 px-4 rounded-lg transition-all ${
      isActive 
        ? "text-purple-600 bg-purple-50" 
        : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
    }`;
  };

  const getMobileButtonClass = (path) => {
    const isActive = location.pathname === path;
    return `flex flex-col items-center justify-center p-3 bg-white rounded-lg shadow-md transition-all ${
      isActive ? "border-l-4 border-purple-600" : ""
    }`;
  };

  // Mobile sidebar that shows small icon buttons on the left side
  const MobileSidebar = () => (
    <div className="fixed left-0 top-1/2 transform -translate-y-1/2 z-10">
      <div className="flex flex-col space-y-3 p-2">
        <Link to="/account" className={getMobileButtonClass("/account")}>
          <FiUser className="text-xl mb-1" />
          <span className="text-xs">Profile</span>
        </Link>
        <Link to="/account/address" className={getMobileButtonClass("/account/address")}>
          <FiMapPin className="text-xl mb-1" />
          <span className="text-xs">Address</span>
        </Link>
        <Link to="/account/orders" className={getMobileButtonClass("/account/orders")}>
          <FiShoppingBag className="text-xl mb-1" />
          <span className="text-xs">Orders</span>
        </Link>
        <button
          onClick={handleLogout}
          className="flex flex-col items-center justify-center p-3 bg-white rounded-lg shadow-md"
        >
          <FiLogOut className="text-xl mb-1" />
          <span className="text-xs">Logout</span>
        </button>
      </div>
    </div>
  );

  // Desktop sidebar with full text links
  const DesktopSidebar = () => (
    <div className="bg-white rounded-lg shadow-md p-4 w-64">
      <div className="flex flex-col space-y-2">
        <h3 className="font-medium text-lg mb-4">My Account</h3>
        <Link to="/account" className={getLinkClass("/account")}>
          <FiUser className="text-xl" />
          <span>Profile</span>
        </Link>
        <Link to="/account/address" className={getLinkClass("/account/address")}>
          <FiMapPin className="text-xl" />
          <span>Address</span>
        </Link>
        <Link to="/account/orders" className={getLinkClass("/account/orders")}>
          <FiShoppingBag className="text-xl" />
          <span>Orders</span>
        </Link>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 py-2 px-4 text-red-500 hover:text-red-700 rounded-lg hover:bg-red-50 transition-all mt-6"
        >
          <FiLogOut className="text-xl" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );

  // Mobile menu button that appears on top right corner for very small screens
  const MobileMenuButton = () => (
    <button
      onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      className="fixed top-24 right-4 z-20 bg-purple-600 text-white p-3 rounded-full shadow-lg md:hidden"
    >
      {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
    </button>
  );

  // Full screen mobile menu that slides in
  const MobileFullMenu = () => (
    <div className={`fixed inset-0 bg-white z-50 transform transition-transform duration-300 ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
      <div className="flex flex-col h-full p-6">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-xl font-semibold">Account Menu</h2>
          <button onClick={() => setIsMobileMenuOpen(false)}>
            <FiX size={24} />
          </button>
        </div>
        <div className="flex flex-col space-y-4">
          <Link 
            to="/account" 
            className="text-lg py-3 border-b" 
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <FiUser className="inline mr-3" />
            Profile
          </Link>
          <Link 
            to="/account/address" 
            className="text-lg py-3 border-b" 
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <FiMapPin className="inline mr-3" />
            Address
          </Link>
          <Link 
            to="/account/orders" 
            className="text-lg py-3 border-b" 
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <FiShoppingBag className="inline mr-3" />
            Orders
          </Link>
          <button
            onClick={() => {
              handleLogout();
              setIsMobileMenuOpen(false);
            }}
            className="text-lg py-3 text-red-500 flex items-center"
          >
            <FiLogOut className="inline mr-3" />
            Logout
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* For extra small screens, show hamburger menu button and full screen menu */}
      <div className="block sm:hidden">
        <MobileMenuButton />
        <MobileFullMenu />
      </div>
      
      {/* For small screens, show side icon bar */}
      <div className="hidden sm:block md:hidden">
        <MobileSidebar />
      </div>
      
      {/* For medium and larger screens, show full sidebar */}
      <div className="hidden md:block">
        <DesktopSidebar />
      </div>
    </>
  );
};

export default Sidebar;